'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

interface NotificationContextProps {
  // General pip management
  setPip: (tab: 'chat' | 'filters' | 'dashboard' | 'quickFilters' | 'profile', value: boolean | number) => void;
  clearPip: (tab: 'chat' | 'filters' | 'dashboard' | 'quickFilters' | 'profile') => void;
  clearAllPips: () => void;
  
  // Pip state - quickFilters is numerical, others are boolean
  pips: {
    chat: boolean;
    filters: boolean;
    dashboard: boolean;
    quickFilters: number;
    profile: boolean;
  };
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pips, setPips] = useState({
    chat: false,
    filters: false,
    dashboard: false,
    quickFilters: 0,
    profile: false,
  });

  // Helper functions for pip management
  const setPip = useCallback((tab: keyof typeof pips, value: boolean | number) => {
    setPips(prev => ({
      ...prev,
      [tab]: value
    }));
  }, []);

  const clearPip = useCallback((tab: keyof typeof pips) => {
    setPips(prev => ({
      ...prev,
      [tab]: tab === 'quickFilters' ? 0 : false
    }));
  }, []);

  const clearAllPips = useCallback(() => {
    setPips({
      chat: false,
      filters: false,
      dashboard: false,
      quickFilters: 0,
      profile: false,
    });
  }, []);

  const contextValue = React.useMemo(() => ({
    setPip,
    clearPip,
    clearAllPips,
    pips,
  }), [setPip, clearPip, clearAllPips, pips]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};
